import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * 알림 타입 정의
 */
export type NotificationType = "success" | "error" | "warning" | "info";

/**
 * 알림 인터페이스
 */
export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number; // ms, 0이면 자동으로 사라지지 않음
  action?: {
    label: string;
    onClick: () => void;
  };
  timestamp: number;
}

/**
 * 알림 생성을 위한 입력 타입
 */
export interface NotificationInput {
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * 알림 상태 인터페이스
 */
interface NotificationState {
  // 알림 목록
  notifications: Notification[];

  // 최대 알림 개수
  maxNotifications: number;

  // 액션
  addNotification: (input: NotificationInput) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;

  // 편의 메서드
  showSuccess: (message: string, title?: string, duration?: number) => string;
  showError: (message: string, title?: string, duration?: number) => string;
  showWarning: (message: string, title?: string, duration?: number) => string;
  showInfo: (message: string, title?: string, duration?: number) => string;
}

/**
 * ID 생성 함수
 */
const generateId = (): string => {
  return `notification_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;
};

/**
 * 슈퍼어드민 알림 상태 관리 Store
 */
export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      // 초기 상태
      notifications: [],
      maxNotifications: 5,

      // 알림 추가
      addNotification: (input) => {
        const id = generateId();
        const notification: Notification = {
          id,
          type: input.type,
          title: input.title,
          message: input.message,
          duration: input.duration ?? 5000, // 기본 5초
          action: input.action,
          timestamp: Date.now(),
        };

        console.log("📢 notificationStore.addNotification:", notification);

        set((state) => {
          const newNotifications = [notification, ...state.notifications];

          // 최대 개수 제한
          if (newNotifications.length > state.maxNotifications) {
            newNotifications.splice(state.maxNotifications);
          }

          return { notifications: newNotifications };
        });

        // 자동 제거 (duration이 0이 아닌 경우)
        if (notification.duration && notification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration);
        }

        return id;
      },

      // 알림 제거
      removeNotification: (id) => {
        console.log("🗑️ notificationStore.removeNotification:", id);
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      // 모든 알림 제거
      clearAllNotifications: () => {
        console.log("🧹 notificationStore.clearAllNotifications");
        set({ notifications: [] });
      },

      // 성공 알림
      showSuccess: (message, title = "성공", duration = 4000) => {
        return get().addNotification({
          type: "success",
          title,
          message,
          duration,
        });
      },

      // 오류 알림
      showError: (message, title = "오류", duration = 6000) => {
        return get().addNotification({
          type: "error",
          title,
          message,
          duration,
        });
      },

      // 경고 알림
      showWarning: (message, title = "경고", duration = 5000) => {
        return get().addNotification({
          type: "warning",
          title,
          message,
          duration,
        });
      },

      // 정보 알림
      showInfo: (message, title = "알림", duration = 4000) => {
        return get().addNotification({
          type: "info",
          title,
          message,
          duration,
        });
      },
    }),
    {
      name: "super-admin-notification-store",
    }
  )
);
